/**
 * KPI API
 *
 * API functions for KPI tracking feature.
 * Transforms camelCase responses from backend to snake_case internal types.
 */

import { api } from '@/lib/api';
import type {
  KpiDefinition,
  KpiDefinitionsResponse,
  KpiValue,
  KpiValuesResponse,
  ScorecardKpi,
  ScorecardPeriodValue,
  ScorecardCategory,
  ScorecardPeriod,
  ScorecardResponse,
  CompletionStatsResponse,
  CompletionStatsFilters,
  CreateKpiDefinitionInput,
  UpdateKpiDefinitionInput,
  CreateKpiValueInput,
  CreateKpiValuesBulkInput,
  UpdateKpiValueInput,
  KpiDefinitionFilters,
  KpiValueFilters,
  ScorecardFilters,
  KpiCategory,
  KpiFrequency,
  KpiPeriodType,
  NumberFormat,
  OrientationRule,
  IndicatorType,
} from './types';

// =============================================================================
// API Response Types (camelCase from backend)
// =============================================================================

interface ApiKpiDefinition {
  id: string;
  name: string;
  description: string | null;
  category: KpiCategory;
  goalUnit: string | null;
  goalValue: number | null;
  goalMin: number | null;
  goalMax: number | null;
  orientationRule: OrientationRule;
  frequency: KpiFrequency;
  numberFormat: NumberFormat;
  showTotal: boolean;
  showAverage: boolean;
  showGoal: boolean;
  ownerId: string | null;
  ownerName: string | null;
  organisationId: string;
  accountId: string | null;
  teamIds: string[];
  indicatorType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiKpiDefinitionsResponse {
  definitions: ApiKpiDefinition[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface ApiKpiValue {
  id: string;
  kpiId: string;
  organisationId: string;
  accountId: string | null;
  datetime: string;
  value: number;
  notes: string | null;
  isArchived: boolean;
  createdAt: string;
  kpiName?: string;
  kpiCategory?: KpiCategory;
  kpiUnit?: string | null;
}

interface ApiKpiValuesResponse {
  values: ApiKpiValue[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/** Scorecard period value from API */
interface ApiScorecardPeriodValue {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  value: number | null;
  valueSum: number | null;
  valueAvg: number | null;
  valueCount: number;
  onTrack: boolean | null;
  snapshotId: string | null;
  notes: string | null;
}

/** Scorecard KPI from API */
interface ApiScorecardKpi {
  id: string;
  name: string;
  description: string | null;
  ownerId: string | null;
  ownerName: string | null;
  goalValue: number | null;
  goalUnit: string | null;
  goalMin: number | null;
  goalMax: number | null;
  orientationRule: OrientationRule;
  numberFormat: NumberFormat;
  showTotal: boolean;
  showAverage: boolean;
  showGoal: boolean;
  indicatorType: string;
  teamIds: string[];
  color: string | null;
  currentValue: number | null;
  average: number | null;
  total: number | null;
  onTrack: boolean | null;
  values: ApiScorecardPeriodValue[];
}

/** Scorecard category from API */
interface ApiScorecardCategory {
  name: KpiCategory;
  kpis: ApiScorecardKpi[];
}

/** Scorecard period from API */
interface ApiScorecardPeriod {
  label: string;
  start: string;
  end: string;
}

interface ApiScorecardResponse {
  categories: ApiScorecardCategory[];
  periods: ApiScorecardPeriod[];
  periodType: KpiPeriodType;
  organisationId: string;
  accountId: string | null;
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformDefinition(data: ApiKpiDefinition): KpiDefinition {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    category: data.category,
    goal_unit: data.goalUnit,
    goal_value: data.goalValue,
    goal_min: data.goalMin,
    goal_max: data.goalMax,
    orientation_rule: data.orientationRule,
    frequency: data.frequency,
    number_format: data.numberFormat || 'auto',
    show_total: data.showTotal,
    show_average: data.showAverage,
    show_goal: data.showGoal,
    owner_id: data.ownerId,
    owner_name: data.ownerName,
    organisation_id: data.organisationId,
    account_id: data.accountId ?? null,
    team_ids: data.teamIds ?? [],
    indicator_type: data.indicatorType as IndicatorType,
    is_active: data.isActive,
    created_at: data.createdAt,
    updated_at: data.updatedAt,
  };
}

function transformDefinitionsResponse(data: ApiKpiDefinitionsResponse): KpiDefinitionsResponse {
  return {
    definitions: data.definitions.map(transformDefinition),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

function transformValue(data: ApiKpiValue): KpiValue {
  return {
    id: data.id,
    kpi_id: data.kpiId,
    organisation_id: data.organisationId,
    account_id: data.accountId ?? null,
    datetime: data.datetime,
    value: data.value,
    notes: data.notes,
    is_archived: data.isArchived,
    created_at: data.createdAt,
    kpi_name: data.kpiName,
    kpi_category: data.kpiCategory,
    kpi_unit: data.kpiUnit,
  };
}

function transformValuesResponse(data: ApiKpiValuesResponse): KpiValuesResponse {
  return {
    values: data.values.map(transformValue),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

function transformScorecardPeriodValue(data: ApiScorecardPeriodValue): ScorecardPeriodValue {
  return {
    period_start: data.periodStart,
    period_end: data.periodEnd,
    period_label: data.periodLabel,
    value: data.value,
    value_sum: data.valueSum,
    value_avg: data.valueAvg,
    value_count: data.valueCount,
    on_track: data.onTrack,
    snapshot_id: data.snapshotId,
    notes: data.notes ?? null,
  };
}

function transformScorecardKpi(data: ApiScorecardKpi): ScorecardKpi {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    owner_id: data.ownerId ?? null,
    owner_name: data.ownerName,
    goal_value: data.goalValue,
    goal_unit: data.goalUnit,
    goal_min: data.goalMin,
    goal_max: data.goalMax,
    orientation_rule: data.orientationRule,
    number_format: data.numberFormat || 'auto',
    show_total: data.showTotal,
    show_average: data.showAverage,
    show_goal: data.showGoal,
    indicator_type: data.indicatorType as IndicatorType,
    team_ids: data.teamIds ?? [],
    color: data.color,
    current_value: data.currentValue,
    average: data.average,
    total: data.total,
    on_track: data.onTrack,
    values: data.values.map(transformScorecardPeriodValue),
  };
}

function transformScorecardCategory(data: ApiScorecardCategory): ScorecardCategory {
  return {
    name: data.name,
    kpis: data.kpis.map(transformScorecardKpi),
  };
}

function transformScorecardPeriod(data: ApiScorecardPeriod): ScorecardPeriod {
  return {
    label: data.label,
    start: data.start,
    end: data.end,
  };
}

function transformScorecardResponse(data: ApiScorecardResponse): ScorecardResponse {
  return {
    categories: data.categories.map(transformScorecardCategory),
    periods: data.periods.map(transformScorecardPeriod),
    period_type: data.periodType,
    organisation_id: data.organisationId,
    account_id: data.accountId ?? null,
  };
}

// =============================================================================
// Helper: Build Query Params
// =============================================================================

function buildDefinitionQueryParams(filters?: KpiDefinitionFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.category) params.category = filters.category;
  if (filters?.frequency) params.frequency = filters.frequency;
  if (filters?.is_active !== undefined) params.isActive = filters.is_active;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction !== undefined) params.sortDirection = filters.sort_direction;
  return params;
}

function buildValueQueryParams(filters?: KpiValueFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.kpi_id) params.kpiId = filters.kpi_id;
  if (filters?.from_date) params.fromDate = filters.from_date;
  if (filters?.to_date) params.toDate = filters.to_date;
  if (filters?.is_archived !== undefined) params.isArchived = filters.is_archived;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction !== undefined) params.sortDirection = filters.sort_direction;
  return params;
}

function buildScorecardQueryParams(filters?: ScorecardFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.category) params.category = filters.category;
  if (filters?.period_type) params.periodType = filters.period_type;
  if (filters?.team_id) params.teamId = filters.team_id;
  if (filters?.owner_ids?.length) params.ownerIds = filters.owner_ids.join(',');
  return params;
}

// =============================================================================
// KPI Definition API Functions
// =============================================================================

export async function getKpiDefinitions(filters?: KpiDefinitionFilters): Promise<KpiDefinitionsResponse> {
  const params = buildDefinitionQueryParams(filters);
  const data = await api.get<ApiKpiDefinitionsResponse>('/v4/kpis/definitions', params);
  return transformDefinitionsResponse(data);
}

export async function getKpiDefinition(id: string): Promise<KpiDefinition> {
  const data = await api.get<ApiKpiDefinition>(`/v4/kpis/definitions/${id}`);
  return transformDefinition(data);
}

export async function createKpiDefinition(input: CreateKpiDefinitionInput): Promise<KpiDefinition> {
  const body = {
    name: input.name,
    description: input.description,
    category: input.category,
    goalUnit: input.goal_unit,
    goalValue: input.goal_value,
    goalMin: input.goal_min,
    goalMax: input.goal_max,
    orientationRule: input.orientation_rule,
    frequency: input.frequency,
    numberFormat: input.number_format,
    showTotal: input.show_total,
    showAverage: input.show_average,
    showGoal: input.show_goal,
    ownerId: input.owner_id,
    ownerName: input.owner_name,
    teamIds: input.team_ids,
    indicatorType: input.indicator_type,
  };
  const data = await api.post<ApiKpiDefinition>('/v4/kpis/definitions', body);
  return transformDefinition(data);
}

export async function updateKpiDefinition(id: string, input: UpdateKpiDefinitionInput): Promise<KpiDefinition> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.category !== undefined) body.category = input.category;
  if (input.goal_unit !== undefined) body.goalUnit = input.goal_unit;
  if (input.goal_value !== undefined) body.goalValue = input.goal_value;
  if (input.goal_min !== undefined) body.goalMin = input.goal_min;
  if (input.goal_max !== undefined) body.goalMax = input.goal_max;
  if (input.orientation_rule !== undefined) body.orientationRule = input.orientation_rule;
  if (input.frequency !== undefined) body.frequency = input.frequency;
  if (input.number_format !== undefined) body.numberFormat = input.number_format;
  if (input.show_total !== undefined) body.showTotal = input.show_total;
  if (input.show_average !== undefined) body.showAverage = input.show_average;
  if (input.show_goal !== undefined) body.showGoal = input.show_goal;
  if (input.owner_id !== undefined) body.ownerId = input.owner_id;
  if (input.owner_name !== undefined) body.ownerName = input.owner_name;
  if (input.team_ids !== undefined) body.teamIds = input.team_ids;
  if (input.indicator_type !== undefined) body.indicatorType = input.indicator_type;
  if (input.is_active !== undefined) body.isActive = input.is_active;

  const data = await api.put<ApiKpiDefinition>(`/v4/kpis/definitions/${id}`, body);
  return transformDefinition(data);
}

export async function deleteKpiDefinition(id: string): Promise<void> {
  await api.delete(`/v4/kpis/definitions/${id}`);
}

// =============================================================================
// KPI Value API Functions
// =============================================================================

export async function getKpiValues(filters?: KpiValueFilters): Promise<KpiValuesResponse> {
  const params = buildValueQueryParams(filters);
  const data = await api.get<ApiKpiValuesResponse>('/v4/kpis/values', params);
  return transformValuesResponse(data);
}

export async function getKpiValue(id: string): Promise<KpiValue> {
  const data = await api.get<ApiKpiValue>(`/v4/kpis/values/${id}`);
  return transformValue(data);
}

export async function createKpiValue(input: CreateKpiValueInput): Promise<KpiValue> {
  const body = {
    kpiId: input.kpi_id,
    datetime: input.datetime,
    value: input.value,
    notes: input.notes,
  };
  const data = await api.post<ApiKpiValue>('/v4/kpis/values', body);
  return transformValue(data);
}

/**
 * Upsert a KPI value for a period (replaces existing values in the same period)
 * This is the preferred method for scorecard entry where one value per period is expected.
 */
export async function upsertKpiValue(input: CreateKpiValueInput): Promise<KpiValue> {
  const body = {
    kpiId: input.kpi_id,
    datetime: input.datetime,
    value: input.value,
    notes: input.notes,
  };
  const data = await api.post<ApiKpiValue>('/v4/kpis/values/upsert', body);
  return transformValue(data);
}

export async function createKpiValuesBulk(input: CreateKpiValuesBulkInput): Promise<KpiValue[]> {
  const body = {
    kpiId: input.kpi_id,
    values: input.values.map((v) => ({
      datetime: v.datetime,
      value: v.value,
      notes: v.notes,
    })),
  };
  const data = await api.post<ApiKpiValue[]>('/v4/kpis/values/bulk', body);
  return data.map(transformValue);
}

export async function updateKpiValue(id: string, input: UpdateKpiValueInput): Promise<KpiValue> {
  const body: Record<string, unknown> = {};
  if (input.datetime !== undefined) body.datetime = input.datetime;
  if (input.value !== undefined) body.value = input.value;
  if (input.notes !== undefined) body.notes = input.notes;

  const data = await api.put<ApiKpiValue>(`/v4/kpis/values/${id}`, body);
  return transformValue(data);
}

export async function archiveKpiValue(id: string): Promise<void> {
  await api.delete(`/v4/kpis/values/${id}`);
}

/**
 * Clear (nullify) a scorecard cell by archiving every value recorded for a KPI
 * within the given period. The server recomputes the snapshot so the cell no
 * longer contributes to average / total.
 */
export async function clearKpiPeriod(input: {
  kpi_id: string;
  period_start: string;
  period_end: string;
}): Promise<{ archived_count: number }> {
  return api.post<{ archived_count: number }>('/v4/kpis/values/clear', input);
}

// =============================================================================
// KPI Scorecard API Functions
// =============================================================================

export async function getKpiScorecard(filters?: ScorecardFilters): Promise<ScorecardResponse> {
  const params = buildScorecardQueryParams(filters);
  const data = await api.get<ApiScorecardResponse>('/v4/kpis/scorecard', params);
  return transformScorecardResponse(data);
}

// =============================================================================
// KPI Completion Stats API Functions
// =============================================================================

interface ApiCompletionStatsResponse {
  period: {
    start: string;
    end: string;
    type: string;
    label: string;
  };
  overall: {
    total: number;
    completed: number;
    percentage: number;
  };
  by_team: Array<{
    team_id: string;
    team_name: string;
    total: number;
    completed: number;
    percentage: number;
  }>;
  by_owner: Array<{
    owner_id: string;
    owner_name: string;
    total: number;
    completed: number;
    percentage: number;
  }>;
}

function buildCompletionStatsQueryParams(filters?: CompletionStatsFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.period_type) params.periodType = filters.period_type;
  if (filters?.period_start) params.periodStart = filters.period_start;
  if (filters?.team_id) params.teamId = filters.team_id;
  return params;
}

function transformCompletionStatsResponse(data: ApiCompletionStatsResponse): CompletionStatsResponse {
  return {
    period: {
      start: data.period.start,
      end: data.period.end,
      type: data.period.type as CompletionStatsResponse['period']['type'],
      label: data.period.label,
    },
    overall: data.overall,
    by_team: data.by_team,
    by_owner: data.by_owner,
  };
}

export async function getKpiCompletionStats(filters?: CompletionStatsFilters): Promise<CompletionStatsResponse> {
  const params = buildCompletionStatsQueryParams(filters);
  const data = await api.get<ApiCompletionStatsResponse>('/v4/kpis/completion-stats', params);
  return transformCompletionStatsResponse(data);
}
